<template>
  <VatValidationView
    mode="transaction"
    :transaction-nr="currentTransactionNr"
    :embedded="isEmbedded"
    :customer="customer"
    :tenant="tenant"
  />
</template>

<script lang="ts" setup>
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import {
  vatValidationApi,
  type TransactionStatusSummary,
  type AuditProtocol,
} from '@/api/vatValidation'
import VatValidationView from '@/components/vat/VatValidationView.vue'

const props = defineProps({
  transactionNr: { type: String, default: '' },
  embedded: { type: Boolean, default: false },
  customer: { type: String, default: '' },
  tenant: { type: String, default: '' },
})

const route = useRoute()
const authStore = getActivePinia() ? useAuthStore() : null
const toast = useToast()

const currentTransactionNr = computed(() => {
  return props.transactionNr || (route.params.transactionNr as string) || ''
})

const isEmbedded = computed(() => {
  return props.embedded || route.query.embedded === 'true' || Boolean(route.meta.embedded)
})

const tenant = computed(
  () =>
    props.tenant ||
    (route.query.tenant as string) ||
    authStore?.userInfo?.activeTenant?.tenantName ||
    undefined,
)
const customer = computed(
  () =>
    props.customer ||
    (route.query.customer as string) ||
    authStore?.userInfo?.activeTenant?.customerNo ||
    undefined,
)

const loading = ref(true)
const fetchError = ref('')
const txSummary = ref<TransactionStatusSummary | null>(null)
const linkedProtocol = ref<AuditProtocol | null>(null)
const isProtocolModalOpen = ref(false)

const addressValidationUrl = computed(() => {
  if (!txSummary.value?.addressNr) return '#'
  const base = `/vat-validation/address/${txSummary.value.addressNr}`
  const params = new URLSearchParams()
  if (isEmbedded.value) params.set('embedded', 'true')
  if (tenant.value) params.set('tenant', tenant.value)
  if (customer.value) params.set('customer', customer.value)
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
})

const isCompletelyCorrect = computed(() => {
  if (!txSummary.value) return false
  if (txSummary.value.status !== 'OFFICIALLY_CONFIRMED') return false
  if (linkedProtocol.value?.result) {
    const { companyNameMatch, streetMatch, zipMatch, cityMatch } = linkedProtocol.value.result
    return companyNameMatch === 'A' && streetMatch === 'A' && zipMatch === 'A' && cityMatch === 'A'
  }
  return txSummary.value.valid
})

const effectiveStatus = computed(() => {
  if (!txSummary.value) return ''
  const status = txSummary.value.status
  if (status === 'OFFICIALLY_CONFIRMED') {
    return isCompletelyCorrect.value ? 'OFFICIALLY_CONFIRMED' : 'PARTIALLY_CONFIRMED'
  }
  if (status === 'OFFICIALLY_NOT_CONFIRMED') {
    if (linkedProtocol.value?.result) {
      const { companyNameMatch, streetMatch, zipMatch, cityMatch } = linkedProtocol.value.result
      const countA = [companyNameMatch, streetMatch, zipMatch, cityMatch].filter(
        (m) => m === 'A',
      ).length
      if (countA > 0) {
        return 'PARTIALLY_CONFIRMED'
      }
    } else if (txSummary.value.history && txSummary.value.history.length > 0) {
      const latest = txSummary.value.history[0]
      const countA = [
        latest.companyNameMatch,
        latest.streetMatch,
        latest.zipMatch,
        latest.cityMatch,
      ].filter((m) => m === 'A').length
      if (countA > 0) {
        return 'PARTIALLY_CONFIRMED'
      }
    }
  }
  return status
})

const effectiveStatusDisplay = computed(() => {
  if (!txSummary.value) return ''
  const eff = effectiveStatus.value
  if (eff === 'OFFICIALLY_CONFIRMED') return 'Ausland: Gültig'
  if (eff === 'PARTIALLY_CONFIRMED') return 'Ausland: Eingeschränkt gültig'
  if (eff === 'OFFICIALLY_NOT_CONFIRMED') return 'Ausland: Ungültig'
  if (eff === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' || eff === 'FORMALLY_VALID') return 'Inland: Gültig'
  if (eff === 'FORMALLY_INVALID' || eff === 'FORMAT_ERROR') {
    if (!txSummary.value.vatId || txSummary.value.status === 'MISSING_VAT_ID') {
      return 'Keine USt-IdNr.'
    }
    return 'Inland: Ungültig'
  }
  if (eff === 'MISSING_VAT_ID') return 'Keine USt-IdNr.'
  return txSummary.value.statusDisplay || eff
})

const statusBadgeColor = computed(() => {
  const status = effectiveStatus.value
  switch (status) {
    case 'OFFICIALLY_CONFIRMED':
      return 'success'
    case 'PARTIALLY_CONFIRMED':
      return 'warning'
    case 'FORMALLY_VALID_NO_OFFICIAL_CHECK':
      return 'info'
    case 'CHECK_NOT_POSSIBLE':
      return 'warning'
    case 'OFFICIALLY_NOT_CONFIRMED':
    case 'FORMALLY_INVALID':
    case 'TECHNICAL_ERROR':
      return 'error'
    default:
      return 'neutral'
  }
})

function getHistoryItemDisplay(item: any): string {
  const isAllA =
    item.allMatchesCodeA === true ||
    (item.companyNameMatch === 'A' &&
      item.streetMatch === 'A' &&
      item.zipMatch === 'A' &&
      item.cityMatch === 'A')
  const hasMatches = Boolean(
    item.companyNameMatch || item.streetMatch || item.zipMatch || item.cityMatch,
  )

  if (isAllA) {
    return 'Ausland: Gültig'
  }

  if (hasMatches) {
    return 'Ausland: Eingeschränkt gültig'
  }

  if (item.status === 'OFFICIALLY_CONFIRMED') {
    return item.statusDisplay || 'Ausland: Gültig'
  }
  if (item.status === 'PARTIALLY_CONFIRMED') {
    return 'Ausland: Eingeschränkt gültig'
  }
  if (item.status === 'OFFICIALLY_NOT_CONFIRMED') {
    return 'Ausland: Ungültig'
  }
  if (item.status === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' || item.status === 'FORMALLY_VALID') {
    return 'Inland: Gültig'
  }
  if (item.status === 'FORMALLY_INVALID' || item.status === 'FORMAT_ERROR') {
    if (item.officialStatusCode === 'MISSING_VAT_ID') {
      return 'Keine USt-IdNr.'
    }
    return 'Inland: Ungültig'
  }
  if (item.status === 'MISSING_VAT_ID') {
    return 'Keine USt-IdNr.'
  }
  return item.statusDisplay || item.status
}

function getHistoryItemColor(status: string, item?: any) {
  if (item) {
    const isAllA =
      item.allMatchesCodeA === true ||
      (item.companyNameMatch === 'A' &&
        item.streetMatch === 'A' &&
        item.zipMatch === 'A' &&
        item.cityMatch === 'A')
    const hasMatches = Boolean(
      item.companyNameMatch || item.streetMatch || item.zipMatch || item.cityMatch,
    )

    if (isAllA) {
      return 'success'
    }

    if (hasMatches) {
      return 'warning'
    }
  }

  if (status === 'OFFICIALLY_CONFIRMED') {
    return 'success'
  }
  switch (status) {
    case 'PARTIALLY_CONFIRMED':
      return 'warning'
    case 'FORMALLY_VALID_NO_OFFICIAL_CHECK':
      return 'info'
    case 'CHECK_NOT_POSSIBLE':
      return 'warning'
    case 'OFFICIALLY_NOT_CONFIRMED':
    case 'FORMALLY_INVALID':
    case 'TECHNICAL_ERROR':
      return 'error'
    default:
      return 'neutral'
  }
}

function formatTimestamp(ts?: string) {
  if (!ts) return '-'
  try {
    return new Date(ts).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ts
  }
}

async function fetchData() {
  if (!currentTransactionNr.value) return
  loading.value = true
  fetchError.value = ''
  try {
    const res = await vatValidationApi.getTransactionStatus(
      currentTransactionNr.value,
      tenant.value,
      customer.value,
    )
    txSummary.value = res.data
    if (res.data.validationId && res.data.addressNr) {
      try {
        const pRes = await vatValidationApi.getAuditProtocol(
          res.data.addressNr,
          res.data.validationId,
          tenant.value,
          customer.value,
        )
        linkedProtocol.value = pRes.data
      } catch {
        // non-critical if linked protocol load fails
      }
    }
  } catch (error: unknown) {
    const err = error as { response?: { status?: number; data?: { error?: string } }; message?: string }
    if (err.response?.status === 404) {
      txSummary.value = null
    } else {
      fetchError.value = err.response?.data?.error || 'Fehler beim Laden des Vorgangsstatus'
    }
  } finally {
    loading.value = false
  }
}

async function viewProtocolById(validationId: string) {
  if (!validationId || !txSummary.value?.addressNr) return
  try {
    const res = await vatValidationApi.getAuditProtocol(
      txSummary.value.addressNr,
      validationId,
      tenant.value,
      customer.value,
    )
    linkedProtocol.value = res.data
    isProtocolModalOpen.value = true
  } catch (error: unknown) {
    const err = error as { message?: string }
    toast.add({
      title: 'Fehler beim Laden des Protokolls',
      description: err.message || 'Fehler beim Laden des Protokolls',
      color: 'error',
    })
  }
}

async function viewProtocol() {
  if (!txSummary.value?.validationId || !txSummary.value?.addressNr) return
  try {
    const res = await vatValidationApi.getAuditProtocol(
      txSummary.value.addressNr,
      txSummary.value.validationId,
      tenant.value,
      customer.value,
    )
    linkedProtocol.value = res.data
    isProtocolModalOpen.value = true
  } catch (error: unknown) {
    const err = error as { message?: string }
    toast.add({
      title: 'Fehler beim Laden des Protokolls',
      description: err.message || 'Fehler beim Laden des Protokolls',
      color: 'error',
    })
  }
}

defineExpose({
  txSummary,
  linkedProtocol,
  isProtocolModalOpen,
  loading,
  fetchError,
  addressValidationUrl,
  effectiveStatus,
  effectiveStatusDisplay,
  statusBadgeColor,
  getHistoryItemDisplay,
  getHistoryItemColor,
  formatTimestamp,
  viewProtocolById,
  viewProtocol,
  fetchData,
})

watch(currentTransactionNr, fetchData)
onMounted(fetchData)
</script>
